// A barra do cartão em cima do campo de texto (desenho da tela de 27/10/2026, §4.1): o lembrete de que o primeiro
// link do que a pessoa escreve vai virar um cartão do site, com o ✕ para tirar. A mesma casca da "Respondendo a"
// do chat (48 px, barra de 3 px `primary/60`, `bg-muted`): duas barras empilhadas leem como a mesma família.
//
// Só desenha o que recebe. Quem decide SE há barra e em que estado é a `BarraDoCartao`:
//   - `buscando`: o domínio já aparece (vem do endereço, sem rede) e o título é "Buscando a prévia…", então a
//     barra não "cresce" quando o título chega;
//   - `pronto`: miniatura de 32 px (ou o `Globe` sem imagem) e o título do site;
//   - `casa`: placa branca com o logo (o logo é transparente e tem marinho: sumiria no fundo) e o nome da tela.
//
// A barra NÃO é um link (o ✕ é o único alvo), não tem `title` e não tem `aria-live`: uma barra que aparece não é
// um anúncio (a mesma regra da pílula do #816), e o foco não vai para ela ao aparecer (no celular tiraria o
// teclado do campo). O ✕ existe nos três estados: quem já sabe que não quer o cartão não espera o site responder.
//
// 🔴 O ✕ leva `sm:h-11 sm:w-11` de propósito: o `size="icon"` traz `sm:h-10 sm:w-10`, e sem o par próprio a área
// de toque cairia dos 44 px do celular para 40 no `sm`. O `-my-1.5` baixa a caixa de margem do botão aos 32 px da
// altura do texto: o toque segue 44 × 44 e a barra fica nos 48 px do desenho (e não nos 60 de `py-2` + 44).
// 🔴 O título leva `dir="auto"`: o servidor não tira U+202E nem U+2066-2069, e um título hostil inverteria o resto
// da linha.
// A imagem chega JÁ validada (`previaDoSite`: só relativa do nosso servidor ou `https://`); aqui só se desenha.
import { Globe, Loader2, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  estado: "buscando" | "pronto" | "casa";
  /** O endereço do link (vira o `data-url`). */
  url: string;
  /** O host do link, em minúsculas, com o `www.`: aparece desde o `buscando`. */
  dominio: string;
  /** O título do site (ou o nome da tela, na `casa`). Em `buscando` não se usa. */
  titulo?: string;
  /** A miniatura do `pronto`; sem ela (ou se falhar ao carregar) entra o `Globe`. */
  imagemUrl?: string | null;
  aoTirar: () => void;
  /** Campo travado (sala fechada, enviando): a barra fica e o ✕ não age. */
  desabilitada?: boolean;
  /** Classes a mais na barra (o portal passa `mb-0`: a pilha dele já tem `space-y-2`). */
  className?: string;
}

export function CartaoDeLinkEmPreparo({
  estado,
  url,
  dominio,
  titulo,
  imagemUrl,
  aoTirar,
  desabilitada,
  className,
}: Props) {
  // Guarda QUAL imagem falhou (não um "falhou" solto): se o endereço mudar, a nova tem a sua chance.
  const [imagemQueFalhou, setImagemQueFalhou] = useState<string | null>(null);
  const miniatura = imagemUrl && imagemUrl !== imagemQueFalhou ? imagemUrl : null;

  return (
    <div
      data-testid="cartao-de-link-em-preparo"
      data-estado={estado}
      data-url={url}
      className={cn(
        "mb-2 flex items-center gap-2 rounded border-l-[3px] border-primary/60 bg-muted py-2 pl-2.5 pr-1 text-xs leading-4",
        className
      )}
    >
      {estado === "buscando" ? (
        <Loader2 aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin text-foreground/75" />
      ) : estado === "casa" ? (
        <span
          aria-hidden="true"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white"
        >
          <img src="/logo.png" alt="" draggable={false} className="h-6 w-6 object-contain" />
        </span>
      ) : miniatura ? (
        <img
          src={miniatura}
          alt=""
          aria-hidden="true"
          draggable={false}
          referrerPolicy="no-referrer"
          onError={() => setImagemQueFalhou(miniatura)}
          data-testid="cartao-de-link-em-preparo-miniatura"
          className="h-8 w-8 shrink-0 rounded bg-foreground/10 object-cover"
        />
      ) : (
        <Globe aria-hidden="true" className="h-4 w-4 shrink-0 text-foreground/75" />
      )}
      <div className="min-w-0 flex-1">
        <div
          dir="auto"
          className="truncate font-medium"
          data-testid="cartao-de-link-em-preparo-titulo"
        >
          {estado === "buscando" ? "Buscando a prévia…" : titulo || dominio}
        </div>
        <p className="truncate text-foreground/75" data-testid="cartao-de-link-em-preparo-dominio">
          {dominio}
        </p>
      </div>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={() => aoTirar()}
        disabled={desabilitada}
        aria-label="Tirar o cartão do site"
        data-testid="cartao-de-link-em-preparo-tirar"
        className="-my-1.5 h-11 w-11 shrink-0 sm:h-11 sm:w-11 md:my-0 md:h-7 md:w-7 [&_svg]:size-3.5 sm:[&_svg]:size-3.5"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
